import * as R from 'remeda';
import { IntlShape, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { DatapointAutomationBlocker } from '@rossum/api-client/automationBlockers';
import {
  isSchemaDatapoint,
  isSchemaDatapointButton,
  isSchemaDatapointEnum,
  SchemaUiConfigEdit,
  SchemaUiConfigType,
} from '@rossum/api-client/schemas';
import { Message } from '@rossum/api-client/shared';
import { datapointBlockerSelector } from '../../../redux/modules/annotation/selectors';
import {
  allMessagesSelector,
  datapointsSelector,
  getVisibleDatapoints,
  messagesStatsSelector,
  pendingValidationIdsSelector,
} from '../../../redux/modules/datapoints/selector';
import { isDatapointHidden } from '../../../redux/modules/datapoints/typedHelpers';
import { schemaMapSelector } from '../../../redux/modules/schema/schemaMapSelector';
import {
  AnyDatapointDataST,
  Children,
  MessagesStats,
  MessagesStatsState,
  MultivalueDatapointDataST,
  SectionDatapointDataST,
  SimpleDatapointDataST,
} from '../../../types/datapoints';
import {
  ButtonDatapointSchema,
  EnumDatapointSchema,
  EnumOption,
} from '../../../types/schema';
import {
  buildDescendantTooltip,
  combineMessagesAndBlockers,
  combineMessagesStatsAndBlockers,
  LabeledMessage,
  MessageOrBlocker,
  MessagesStatsWithBlockers,
  resolveValueSource,
} from './sidebar-infosections/automation-blockers/helpers';

// This is intended as a TEMPORARY object, we need these properties to interface with
// Redux on a lower level
// But eventually we would like to get rid of this dependency
type SidebarItemMeta = {
  datapointIndex: number;
};

export type SidebarSectionModel = {
  id: number;
  label: string;
  hidden: boolean;
  children: (
    | SidebarSimplevalueFieldModel
    | SidebarButtonFieldModel
    | SidebarMultivalueFieldModel
    | SidebarLineItemFieldModel
    | SidebarEnumFieldModel
  )[];
};

export type SidebarSimplevalueFieldModel = {
  kind: 'sidebar-simplevalue-field';
  id: number;
  label: string;
  value: string;
  schemaId: string;
  valueSource: SchemaUiConfigType | 'unset' | 'einvoicing';
  editing: SchemaUiConfigEdit;
  validationSources: string[];
  meta: SidebarItemMeta;
  messagesStats: MessagesStatsWithBlockers | undefined;
  messages: MessageOrBlocker[];
  noRecalculation: boolean | undefined;
  confidenceScore: number | null;
  hidden: boolean;
  required: boolean;
  struct: unknown;
};

export type SidebarButtonFieldModel = {
  kind: 'sidebar-button-field';
  id: number;
  label: string;
  url?: string | null;
  canObtainToken: boolean;
  isPendingValidation: boolean;
  hidden: boolean;
  meta: SidebarItemMeta;
};

export type SidebarEnumFieldModel = {
  kind: 'sidebar-enum-field';
  id: number;
  label: string;
  options: EnumOption[];
  value: string;
  valueOption: EnumOption | undefined;
  validationSources: string[];
  valueSource: SchemaUiConfigType | 'unset' | 'einvoicing';
  meta: SidebarItemMeta;
  messagesStats: MessagesStatsWithBlockers | undefined;
  messages: MessageOrBlocker[];
  clearable: boolean;
  editing: SchemaUiConfigEdit;
  schemaId: string;
  noRecalculation: boolean | undefined;
  confidenceScore: number | null;
  hidden: boolean;
  required: boolean;
  struct: unknown;
};

export type SidebarMultivalueChildFieldModel = {
  kind: 'sidebar-multivalue-child-field';
  parentLabel: string;
  parentSchemaId: string;
  id: number;
  schemaId: string;
  value: string;
  valueSource: SchemaUiConfigType | 'unset' | 'einvoicing';
  editing: SchemaUiConfigEdit;
  validationSources: string[];
  meta: SidebarItemMeta;
  messagesStats: MessagesStatsWithBlockers | undefined;
  messages: MessageOrBlocker[];
  confidenceScore: number | null;
  hidden: boolean;
  required: boolean;
  struct: unknown;
};

export type SidebarMultivalueFieldModel = {
  kind: 'sidebar-multivalue-field';
  id: number;
  schemaId: string;
  children: SidebarMultivalueChildFieldModel[];
  childrenSchemaId: string | undefined;
  label: string;
  validationSources: string[];
  editing: SchemaUiConfigEdit;
  meta: SidebarItemMeta;
  messagesStats: MessagesStatsWithBlockers | undefined;
  messages: MessageOrBlocker[];
  hidden: boolean;
};

export type SidebarLineItemFieldModel = {
  kind: 'sidebar-line-item-field';
  id: number;
  label: string;
  schemaId: string;
  validationSources: string[];
  // TODO: Do we want this dependency?
  // Line items need children IDs to distinguish virtual DPs so we have to have this here
  children: Children[];
  meta: SidebarItemMeta;
  messages: MessageOrBlocker[];
  messagesStats: MessagesStatsWithBlockers | undefined;
  hidden: boolean;
};

const constructSidebarSimplevalueModel = (
  dp: SimpleDatapointDataST,
  messages: Message[] | undefined,
  messagesStats: MessagesStats | undefined,
  blockers: DatapointAutomationBlocker[] | undefined,
  schemaMap: ReturnType<typeof schemaMapSelector>,
  intl: IntlShape
): SidebarSimplevalueFieldModel => {
  const messagesWithBlockers = combineMessagesAndBlockers(
    dp.schemaId,
    messages,
    blockers,
    schemaMap,
    intl
  );

  return {
    kind: 'sidebar-simplevalue-field',
    id: dp.id,
    label: dp.schema?.label ?? '',
    value: dp.content?.value ?? '',
    schemaId: dp.schemaId,
    valueSource: resolveValueSource(dp),
    editing: isSchemaDatapoint(dp.schema)
      ? (dp.schema.uiConfiguration?.edit ?? 'enabled')
      : 'enabled',
    validationSources: dp.validationSources,
    meta: {
      datapointIndex: dp.meta.index,
    },
    messages: messagesWithBlockers,
    messagesStats: combineMessagesStatsAndBlockers(
      messagesStats,
      blockers,
      messagesWithBlockers
    ),
    noRecalculation: dp.noRecalculation,
    confidenceScore: dp.content?.rirConfidence ?? null,
    hidden: isDatapointHidden(dp.hidden, dp.schema?.hidden),
    required: dp.schema?.constraints?.required ?? true,
    struct: dp.content?.struct,
  };
};

const isSimpleValue = (dp: AnyDatapointDataST): dp is SimpleDatapointDataST =>
  isSchemaDatapoint(dp.schema) && !isSchemaDatapointButton(dp.schema);

type SimpleDatapointButtonST = Omit<SimpleDatapointDataST, 'schema'> & {
  schema?: ButtonDatapointSchema;
};

const isButton = (dp: unknown): dp is SimpleDatapointButtonST =>
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  isSchemaDatapointButton((dp as any).schema);

const constructSidebarButtonModel = (
  dp: SimpleDatapointButtonST,
  pendingValidationIds: number[]
): SidebarButtonFieldModel => {
  return {
    kind: 'sidebar-button-field',
    id: dp.id,
    label: dp.schema?.label ?? '',
    url: dp.schema?.popupUrl ?? null,
    canObtainToken: dp.schema?.canObtainToken ?? false,
    isPendingValidation: pendingValidationIds.includes(dp.id),
    hidden: isDatapointHidden(dp.hidden, dp.schema?.hidden),
    meta: {
      datapointIndex: dp.meta.index,
    },
  };
};

type SimpleDatapointEnumST = Omit<SimpleDatapointDataST, 'schema'> & {
  schema?: EnumDatapointSchema;
};

const isEnum = (dp: unknown): dp is SimpleDatapointEnumST =>
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  isSchemaDatapointEnum((dp as any).schema);

const constructSidebarEnumModel = (
  dp: SimpleDatapointEnumST,
  messages: Message[] | undefined,
  messagesStats: MessagesStats | undefined,
  blockers: DatapointAutomationBlocker[] | undefined,
  schemaMap: ReturnType<typeof schemaMapSelector>,
  intl: IntlShape
): SidebarEnumFieldModel => {
  // `label` is typed as a string, but of the backends writing options only the lookup one
  // coerces it, so a null still arrives here — and the dropdown reads `label.length`.
  const options = (dp.options ?? dp.schema?.options ?? []).map(option => ({
    ...option,
    label: option.label ?? '',
  }));
  // If value of enum is not found, it has value === '', but there could be
  // an option with empty string value in the options as well.
  // We want to set this option instead of fallbacking to default value if the empty value option is available
  const isEmptyStringInOptions = options.some(option => option.value === '');

  const required = isSchemaDatapoint(dp.schema)
    ? (dp.schema?.constraints?.required ?? true)
    : true;
  const valueOption = R.find(options, option => {
    const resolvedValue =
      dp.content?.value !== undefined &&
      dp.content.value !== null &&
      (dp.content.value !== '' || isEmptyStringInOptions || !required)
        ? dp.content.value
        : (dp.schema?.defaultValue ?? '');

    return R.toLowerCase(option.value) === R.toLowerCase(resolvedValue);
  });

  const clearable = !isEmptyStringInOptions && !required;

  const messagesWithBlockers = combineMessagesAndBlockers(
    dp.schemaId,
    messages,
    blockers,
    schemaMap,
    intl
  );

  return {
    kind: 'sidebar-enum-field',
    id: dp.id,
    label: dp.schema?.label ?? '',
    meta: {
      datapointIndex: dp.meta.index,
    },
    schemaId: dp.schemaId,
    options,
    value: dp.content?.value ?? '',
    valueOption,
    messages: messagesWithBlockers,
    messagesStats: combineMessagesStatsAndBlockers(
      messagesStats,
      blockers,
      messagesWithBlockers
    ),
    clearable,
    validationSources: dp.validationSources,
    valueSource: resolveValueSource(dp),
    editing: isSchemaDatapoint(dp.schema)
      ? (dp.schema.uiConfiguration?.edit ?? 'enabled')
      : 'enabled',
    noRecalculation: dp.noRecalculation,
    confidenceScore: dp.content?.rirConfidence ?? null,
    hidden: isDatapointHidden(dp.hidden, dp.schema?.hidden),
    required,
    struct: dp.content?.struct,
  };
};

const isLineItem = (dp: AnyDatapointDataST): dp is MultivalueDatapointDataST =>
  dp.category === 'multivalue' && !dp.meta.isSimpleMultivalue;

// Collect descendant messages paired with their column label. Parent-row
// tooltips need these because `allMessages[parentId]` only holds the parent's
// own messages, while `messagesStats[parentId].count` is already aggregated.
const collectDescendantMessages = (
  dp: AnyDatapointDataST,
  allDatapoints: AnyDatapointDataST[],
  allMessages: Record<string, Message[]>
): LabeledMessage[] => {
  if (!('children' in dp)) return [];
  return dp.children.flatMap(child => {
    const childDp = allDatapoints[child.index];
    if (!childDp) return [];
    const label = childDp.schema?.label ?? '';
    const own = (allMessages[childDp.id] ?? []).map(message => ({
      label,
      message,
    }));
    return [
      ...own,
      ...collectDescendantMessages(childDp, allDatapoints, allMessages),
    ];
  });
};

// Resolve the aggregated tooltip for a multivalue/line-item parent row,
// preferring descendant-derived text; fall back to any auto-derived content
// from `combineMessagesStatsAndBlockers`.
const withParentTooltip = (
  stats: MessagesStatsWithBlockers | undefined,
  ownMessages: MessageOrBlocker[],
  descendantMessages: LabeledMessage[],
  intl: IntlShape
): MessagesStatsWithBlockers | undefined => {
  if (!stats) return undefined;
  const ownAsLabeled: LabeledMessage[] = ownMessages
    .filter((m): m is MessageOrBlocker & { content: string } => !!m.content)
    .map(m => ({ label: '', message: { type: 'info', content: m.content } }));
  const tooltipContent = buildDescendantTooltip(
    [...ownAsLabeled, ...descendantMessages],
    intl
  );
  return { ...stats, tooltipContent: tooltipContent ?? stats.tooltipContent };
};

const constructSidebarLineItemModel = (
  dp: MultivalueDatapointDataST,
  messages: Message[] | undefined,
  descendantMessages: LabeledMessage[],
  automationBlockers: DatapointAutomationBlocker[] | undefined,
  schemaMap: ReturnType<typeof schemaMapSelector>,
  messagesStats: MessagesStats | undefined,
  intl: IntlShape
): SidebarLineItemFieldModel => {
  const ownMessages = combineMessagesAndBlockers(
    dp.schemaId,
    messages,
    automationBlockers,
    schemaMap,
    intl
  );
  return {
    kind: 'sidebar-line-item-field',
    id: dp.id,
    label: dp.schema?.label ?? '',
    schemaId: dp.schemaId,
    children: dp.children,
    validationSources: dp.validationSources ?? [],
    meta: {
      datapointIndex: dp.meta.index,
    },
    messages: ownMessages,
    messagesStats: withParentTooltip(
      combineMessagesStatsAndBlockers(
        messagesStats,
        automationBlockers,
        ownMessages
      ),
      ownMessages,
      descendantMessages,
      intl
    ),
    hidden: isDatapointHidden(dp.hidden, dp.schema?.hidden),
  };
};

const constructSidebarMultivalueChildModel = (
  dp: SimpleDatapointDataST,
  parentLabel: string,
  parentSchemaId: string,
  messages: Message[] | undefined,
  messagesStats: MessagesStats | undefined,
  automationBlockers: DatapointAutomationBlocker[] | undefined,
  schemaMap: ReturnType<typeof schemaMapSelector>,
  intl: IntlShape
): SidebarMultivalueChildFieldModel => {
  const multivalueChildMessages = combineMessagesAndBlockers(
    dp.schemaId,
    messages,
    automationBlockers,
    schemaMap,
    intl
  );
  return {
    kind: 'sidebar-multivalue-child-field',
    id: dp.id,
    parentLabel,
    parentSchemaId,
    schemaId: dp.schemaId,
    value: dp.content?.value ?? '',
    valueSource: resolveValueSource(dp),
    editing: isSchemaDatapoint(dp.schema)
      ? (dp.schema.uiConfiguration?.edit ?? 'enabled')
      : 'enabled',
    validationSources: dp.validationSources,
    meta: {
      datapointIndex: dp.meta.index,
    },
    messages: multivalueChildMessages,
    messagesStats: combineMessagesStatsAndBlockers(
      messagesStats,
      automationBlockers,
      multivalueChildMessages
    ),
    confidenceScore: dp.content?.rirConfidence ?? null,
    hidden: isDatapointHidden(dp.hidden, dp.schema?.hidden),
    required: dp.schema?.constraints?.required ?? true,
    struct: dp.content?.struct,
  };
};

const isMultivalue = (
  dp: AnyDatapointDataST
): dp is Omit<MultivalueDatapointDataST, 'grid'> & { grid: never } =>
  dp.category === 'multivalue' && !!dp.meta.isSimpleMultivalue;

const constructSidebarMultivalueModel = (
  dp: MultivalueDatapointDataST,
  allDatapoints: AnyDatapointDataST[],
  allMessages: Record<string, Message[]>,
  allMessagesStats: MessagesStatsState,
  allAutomationBlockers: Record<string, DatapointAutomationBlocker[]>,
  schemaMap: ReturnType<typeof schemaMapSelector>,
  messagesStats: MessagesStats | undefined,
  intl: IntlShape
): SidebarMultivalueFieldModel => {
  const label = dp.schema?.label ?? '';

  const childId = dp.schema?.children?.[0];
  const childSchema = childId ? schemaMap.get(childId) : null;
  const ownMessages = combineMessagesAndBlockers(
    dp.schemaId,
    allMessages[dp.id],
    allAutomationBlockers[dp.schemaId],
    schemaMap,
    intl
  );
  const descendantMessages = collectDescendantMessages(
    dp,
    allDatapoints,
    allMessages
  );

  return {
    kind: 'sidebar-multivalue-field',
    id: dp.id,
    schemaId: dp.schemaId,
    label,
    childrenSchemaId: dp.schema?.children?.[0],
    children: dp.children.flatMap(child => {
      const childDp = allDatapoints[child.index];
      if (childDp && isSimpleValue(childDp)) {
        return constructSidebarMultivalueChildModel(
          childDp,
          label,
          dp.schemaId,
          allMessages[childDp.id],
          allMessagesStats[childDp.id],
          allAutomationBlockers[childDp.schemaId],
          schemaMap,
          intl
        );
      }

      return [];
    }),
    validationSources: dp.validationSources ?? [],
    editing: isSchemaDatapoint(childSchema)
      ? (childSchema.uiConfiguration?.edit ?? 'enabled')
      : 'enabled',
    meta: {
      datapointIndex: dp.meta.index,
    },
    messages: ownMessages,
    messagesStats: withParentTooltip(
      combineMessagesStatsAndBlockers(
        messagesStats,
        allAutomationBlockers[dp.schemaId],
        ownMessages
      ),
      ownMessages,
      descendantMessages,
      intl
    ),
    hidden: isDatapointHidden(dp.hidden, dp.schema?.hidden),
  };
};

const constructSidebarSectionModel = (
  section: SectionDatapointDataST,
  allDatapoints: AnyDatapointDataST[],
  allMessages: {
    [key: number]: Message[];
    all?: Message[];
  },
  allAutomationBlockers: Record<string, DatapointAutomationBlocker[]>,
  schemaMap: ReturnType<typeof schemaMapSelector>,
  messagesStats: MessagesStatsState,
  intl: IntlShape,
  pendingValidationIds: number[]
): SidebarSectionModel => {
  return {
    id: section.id,
    label: section.schema?.label ?? '',
    hidden: isDatapointHidden(section.hidden, section.schema?.hidden),
    children: R.filter(
      section.children.map(({ index }) => {
        const dp = allDatapoints[index];

        if (!dp) {
          return null;
        }

        if (isButton(dp)) {
          return constructSidebarButtonModel(dp, pendingValidationIds);
        }

        if (isEnum(dp)) {
          return constructSidebarEnumModel(
            dp,
            allMessages[dp.id],
            messagesStats[dp.id],
            allAutomationBlockers[dp.schemaId],
            schemaMap,
            intl
          );
        }

        if (isSimpleValue(dp)) {
          return constructSidebarSimplevalueModel(
            dp,
            allMessages[dp.id],
            messagesStats[dp.id],
            allAutomationBlockers[dp.schemaId],
            schemaMap,
            intl
          );
        }

        if (isMultivalue(dp)) {
          return constructSidebarMultivalueModel(
            dp,
            allDatapoints,
            allMessages,
            messagesStats,
            allAutomationBlockers,
            schemaMap,
            messagesStats[dp.id],
            intl
          );
        }

        if (isLineItem(dp)) {
          return constructSidebarLineItemModel(
            dp,
            allMessages[dp.id],
            collectDescendantMessages(dp, allDatapoints, allMessages),
            allAutomationBlockers[dp.schemaId],
            schemaMap,
            messagesStats[dp.id],
            intl
          );
        }

        return null;
      }),
      R.isTruthy
    ),
  };
};

export const useSidebarData = (displayAutomationBlockers: boolean) => {
  const allDatapoints = useSelector(datapointsSelector);

  const visibleDatapoints = useSelector(getVisibleDatapoints);

  const allMessages = useSelector(allMessagesSelector);

  const messagesStats = useSelector(messagesStatsSelector);

  const pendingValidationIds = useSelector(pendingValidationIdsSelector);

  const allAutomationBlockers_ = useSelector(datapointBlockerSelector);

  const allAutomationBlockers = displayAutomationBlockers
    ? allAutomationBlockers_
    : {};

  const schemaMap = useSelector(schemaMapSelector);

  const intl = useIntl();

  const result = visibleDatapoints.reduce<SidebarSectionModel[]>(
    (acc, cur, _idx) => {
      if (cur.category !== 'section' || cur.children.length === 0) {
        return acc;
      }

      return [
        ...acc,
        constructSidebarSectionModel(
          cur,
          allDatapoints,
          allMessages,
          allAutomationBlockers,
          schemaMap,
          messagesStats,
          intl,
          pendingValidationIds
        ),
      ];
    },
    []
  );

  return result;
};
